import {
  PartFinderImageKind,
  PartFinderReport,
  PartFinderReportImageInfo,
  ThumbnailMatchType,
} from '@/api/generatedApi';
import { useGetPartFinderReportImage } from '@/hooks/api/part-finder-reports/useGetPartFinderReportImage';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { ImageOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { CollapsibleSection } from './CollapsibleSection';
import { ReportImageLightbox } from './ReportImageLightbox';

const TILE_SIZE = 132;
const WBPARTS_ATTRIBUTION = '© WBParts';

const ImageFrame = styled(Box)(({ theme }) => ({
  width: TILE_SIZE,
  height: TILE_SIZE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.action.hover,
  borderRadius: '8px',
  overflow: 'hidden',
  cursor: 'pointer',
  '&:hover': { outline: `2px solid ${theme.palette.primary.main}` },
}));

const SEARCH_PHOTO_CAPTION = 'The photo you searched with';

function imageLabel(image: PartFinderReportImageInfo): string {
  if (image.kind === PartFinderImageKind.SearchPhoto) {
    return 'Your photo';
  }
  if (image.kind === PartFinderImageKind.Drawing) {
    return 'Drawing';
  }
  return image.matchType === ThumbnailMatchType.Related ? 'Related product' : 'Exact match';
}

function imageCaption(image: PartFinderReportImageInfo): string {
  if (image.kind === PartFinderImageKind.SearchPhoto) {
    return SEARCH_PHOTO_CAPTION;
  }
  if (image.kind === PartFinderImageKind.Drawing) {
    return `Drawing · ${WBPARTS_ATTRIBUTION}`;
  }
  if (image.matchType === ThumbnailMatchType.Related) {
    return `Related product — not the searched NSN · ${WBPARTS_ATTRIBUTION}`;
  }
  return `Exact match · ${WBPARTS_ATTRIBUTION}`;
}

interface ReportImageTileProperties {
  reportId: string;
  version?: string;
  image: PartFinderReportImageInfo;
  onClick: () => void;
}

const ReportImageTile = ({ reportId, version, image, onClick }: ReportImageTileProperties) => {
  const [hasError, setHasError] = useState(false);
  const { image: imageUrl, isLoading } = useGetPartFinderReportImage({ reportId, index: image.index, version });

  if (hasError || (!isLoading && !imageUrl)) {
    return <></>;
  }

  const caption = imageCaption(image);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: TILE_SIZE }}>
      <ImageFrame onClick={onClick} role='button' aria-label={`View ${caption}`} title={caption}>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={caption}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onError={() => setHasError(true)}
          />
        )}
      </ImageFrame>
      <Typography variant='caption' noWrap sx={{ color: 'text.secondary' }}>
        {imageLabel(image)}
      </Typography>
    </Box>
  );
};

interface ReportImagesProperties {
  report: PartFinderReport;
}

export const ReportImages = ({ report }: ReportImagesProperties) => {
  const { isPartFinderThumbnailsEnabled } = useFeatureFlags();
  const [activePosition, setActivePosition] = useState<number | undefined>();
  const images = report.images ?? [];

  useEffect(() => {
    setActivePosition(undefined);
  }, [report.id]);

  if (!isPartFinderThumbnailsEnabled || images.length === 0) {
    return <></>;
  }

  const activeImage = activePosition === undefined ? undefined : images[activePosition];

  return (
    <CollapsibleSection icon={<ImageOutlined />} title='Images'>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {images.map((image, position) => (
          <ReportImageTile
            key={image.index}
            reportId={report.id}
            version={report.updatedAt}
            image={image}
            onClick={() => setActivePosition(position)}
          />
        ))}
      </Box>
      {images.some(image => image.kind !== PartFinderImageKind.SearchPhoto) && (
        <Typography variant='caption' sx={{ display: 'block', marginTop: '10px', color: 'text.secondary' }}>
          Images sourced from {WBPARTS_ATTRIBUTION}, Inc. — related products are not the searched NSN.
        </Typography>
      )}
      {activePosition !== undefined && activeImage !== undefined && (
        <ReportImageLightbox
          reportId={report.id}
          version={report.updatedAt}
          images={images}
          activePosition={activePosition}
          caption={imageCaption(activeImage)}
          onNavigate={setActivePosition}
          onClose={() => setActivePosition(undefined)}
        />
      )}
    </CollapsibleSection>
  );
};
